import React, { useState, useEffect, useCallback } from 'react';
import { api, uploadImage, getImageUrl } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoImagesOutline, IoChevronDownOutline, IoChevronUpOutline, IoTrashOutline } from 'react-icons/io5';

function toStoredAssetPath(uploadedUrl) {
  if (!uploadedUrl || typeof uploadedUrl !== 'string') return '';
  const s = uploadedUrl.trim();
  if (s.startsWith('/uploads')) return s.split('?')[0];
  try {
    const u = new URL(s);
    if (u.pathname.startsWith('/uploads')) return `${u.pathname}${u.search}`;
  } catch (_) {}
  return s;
}

export default function AdminHomeGallery() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return api('/api/admin/home-gallery')
      .then(setList)
      .catch((e) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onPickFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setUploadBusy(true);
    setError('');
    try {
      const fullUrl = await uploadImage(file);
      const image_url = toStoredAssetPath(fullUrl);
      if (!image_url) throw new Error('Invalid upload URL');
      await api('/api/admin/home-gallery', {
        method: 'POST',
        body: JSON.stringify({ image_url, alt_text: '', caption: null }),
      });
      await load();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadBusy(false);
    }
  };

  const saveCell = async (row, fields) => {
    setError('');
    try {
      await api(`/api/admin/home-gallery/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify(fields),
      });
      await load();
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  const move = async (index, dir) => {
    const next = list.slice();
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    const t = next[index];
    next[index] = next[j];
    next[j] = t;
    setList(next);
    const ids = next.map((r) => r.id);
    setError('');
    try {
      await api('/api/admin/home-gallery/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      });
      await load();
    } catch (e) {
      setError(e.message || 'Reorder failed');
      await load();
    }
  };

  const del = async (row) => {
    if (!window.confirm('Remove this image from the homepage gallery?')) return;
    setError('');
    try {
      await api(`/api/admin/home-gallery/${row.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Card>
      <CardHeader
        title="Home gallery"
        subheader={
          <>
            Shown on the homepage after <strong>From the blog</strong>. Upload images — they are stored under{' '}
            <code>/uploads/</code>.
          </>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="contained" size="small" component="label" disabled={uploadBusy}>
              {uploadBusy ? 'Uploading…' : 'Upload image'}
              <input type="file" accept="image/*" hidden onChange={onPickFile} />
            </Button>
          </Box>
        }
      />
      <CardContent>
        {error && (
          <Typography color="error" className="admin-error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {list.length === 0 ? (
          <AdminEmptyState message="No images yet. Upload to show a gallery strip on the home page." icon={IoImagesOutline} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={100}>Preview</TableCell>
                  <TableCell>Alt text</TableCell>
                  <TableCell>Caption</TableCell>
                  <TableCell align="right">Order / actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row, i) => {
                  const thumb = getImageUrl(row.image_url);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {thumb ? (
                          <Box
                            component="img"
                            src={thumb}
                            alt=""
                            sx={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 1 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          defaultValue={row.alt_text || ''}
                          placeholder="Describe the image"
                          key={`alt-${row.id}`}
                          onBlur={(ev) => {
                            const alt_text = ev.target.value;
                            if (alt_text !== (row.alt_text || ''))
                              saveCell(row, { alt_text });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          defaultValue={row.caption || ''}
                          placeholder="Optional caption"
                          key={`cap-${row.id}`}
                          onBlur={(ev) => {
                            const caption = ev.target.value;
                            if (caption !== (row.caption || ''))
                              saveCell(row, { caption: caption.trim() === '' ? null : caption });
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">
                              <IoChevronUpOutline />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={i >= list.length - 1}
                              onClick={() => move(i, 1)}
                              aria-label="Move down"
                            >
                              <IoChevronDownOutline />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => del(row)} aria-label="Delete">
                            <IoTrashOutline />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
